<template>
    <nav class="pagination">
        <a role="button" v-show="!offsetPagination.isFirstPage" class="title-l" @click="prev"><ArrowBlack class="icon-flipped" alt="arrow black" /></a>
        <a role="button" v-if="!firstPageIsVisible && !simple" :class="`title-l ${offsetPagination.currentPage === 1 ? 'active' : ''}`" :aria-current="offsetPagination.currentPage === 1" @click="setPage(1)">
            1
        </a>
        <span v-if="selectedPage > 4 && !simple" class="title-l" aria-hidden="true">...</span>
        <a v-show="!simple" v-for="page in pages" :key="`page-${page}`" 
            :class="`title-l ${selectedPage === page ? 'active' : ''}`" 
            :aria-current="selectedPage === page" 
            :aria-label="`Page ${page} of ${offsetPagination.pageCount}`"
            tabindex="0"
            role="button"
            @click="setPage(page)"
            >
            {{ page }}
        </a>
        <span v-if="endPage < pagination.maxPages - 1 && !simple" aria-hidden="true" class="title-l">...</span>
        <a role="button" v-if="!lastPageIsVisible && !simple" :class="`title-l ${selectedPage === pagination.maxPages ? 'active' : ''}`" :aria-current="pagination.maxPages === selectedPage" @click="setPage(pagination.maxPages)">{{ pagination.maxPages }}</a>
        <a role="button" v-show="!offsetPagination.isLastPage && totalPages > 1" class="title-l" @click="next" :aria-current="selectedPage === 1"><ArrowBlack alt="arrow black" /></a>
    </nav>
</template>

<script lang="ts" setup>
    import { useOffsetPagination } from "@vueuse/core";
    import { useScroll } from '@vueuse/core'
    import { computed, ref, watch } from "vue";
    import ArrowBlack from "@icons/arrow-black-right.svg";

    interface Pagination {
        totalItems: number;
        currentPage: number;
        pageSize: number;
        maxPages: number;
    }

    let props = defineProps({
        pagination: {
            type: Object as PropType<Pagination>,
            default: () => ({
                totalItems: 0,
                currentPage: 1,
                pageSize: 10,
                maxPages: 10
            })
        },
        scrollToTopTarget: {
            type: Object,
            default(){
                return null
            }
        },
        simple: {
            type: Boolean,
            default: false
        }
    });

    const offsetPagination = computed(()=>{
        const {
            currentPage,
            pageCount,
            isFirstPage,
            isLastPage
        } = useOffsetPagination({
            total: props.pagination.totalItems,
            page: props.pagination.currentPage,
            pageSize: props.pagination.pageSize,
            onPageChange: setPage,
        })
        return {
            currentPage: currentPage.value,
            pageCount: pageCount.value,
            isFirstPage: isFirstPage.value,
            isLastPage: isLastPage.value
        }
    })

    let selectedPage = ref(props.pagination.currentPage);

    const scrollToTop = ()=>{
        const { y } = useScroll((props.scrollToTopTarget ?? window), { behavior: 'smooth' })
        y.value = 0;
    }

    let pages = computed(()=>{
       // if currentPage is less than 3, 
        let pages:number[] = [];
        if (selectedPage.value <= 4) {
            for(let i = 1; i <= 5 && i <= props.pagination.maxPages; i++){
                pages.push(i);
            }
        }else if(selectedPage.value + 3 >= props.pagination.maxPages){
            for(let i =  props.pagination.maxPages - 4; i <=  props.pagination.maxPages; i++){
                pages.push(i);
            }
        }else{
            for(let i = selectedPage.value - 2; i <= selectedPage.value + 2; i++){
                pages.push(i);
            }
        }
        return pages;
    });



    let firstPageIsVisible= computed(()=>{
        return selectedPage.value <= 4;
    });


    let lastPageIsVisible = computed(()=>{
        return selectedPage.value + 3 >= props.pagination.maxPages;
    })

    let endPage = computed(()=>{
        return pages.value[pages.value.length - 1];
    })

    let totalPages = computed(()=>{
        return props.pagination.maxPages;
    })

    let emit = defineEmits(["pageChange"]);

    function setPage(page){
        selectedPage.value = page;
        scrollToTop();
        emit('pageChange', page);
    }

    function prev(){
        if(selectedPage.value > 1){
            selectedPage.value -= 1;
            scrollToTop();
            emit('pageChange', selectedPage.value);
        }
    }

    function next(){
        if(selectedPage.value < props.pagination.maxPages){
            selectedPage.value += 1;
            scrollToTop();
            emit('pageChange', selectedPage.value);
        }
    }
    

    watch(() => props.pagination, (newVal) => {
        selectedPage.value = newVal.currentPage;
    }, { immediate: true });
</script>
<style lang="scss" scoped>
.icon-flipped {
    transform: scaleX(-1);
    -moz-transform: scaleX(-1);
    -webkit-transform: scaleX(-1);
    -ms-transform: scaleX(-1);
}
</style>
